/**
 * Full-height left drawer listing the server action log (chronological).
 * Expanded card preview uses the same fixed screen position and scale as hand/deployed hover (from BattleUI).
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../OrderDesign';
import BattleActionLogRow from './BattleActionLogRow';

export default function BattleActionLogDrawer({
  open,
  onClose,
  actionLog,
  gameState,
  userId,
  opponentId,
  currentUsername,
  opponentUsername,
  isMobile,
  fixedDesktopExpandedPosition = null,
  orderDesignScale = 1
}) {
  const { t } = useTranslation();
  const scrollRef = useRef(null);
  const clearTimerRef = useRef(null);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const cancelClearExpand = useCallback(() => {
    if (clearTimerRef.current) {
      clearTimeout(clearTimerRef.current);
      clearTimerRef.current = null;
    }
  }, []);

  const scheduleClearExpand = useCallback(() => {
    cancelClearExpand();
    clearTimerRef.current = setTimeout(() => {
      setExpandedOrder(null);
      clearTimerRef.current = null;
    }, 200);
  }, [cancelClearExpand]);

  const onExpandOrder = useCallback(
    (order) => {
      cancelClearExpand();
      setExpandedOrder(order);
    },
    [cancelClearExpand]
  );

  const onExpandOrderClear = useCallback(() => {
    scheduleClearExpand();
  }, [scheduleClearExpand]);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !open) return;
    el.scrollTop = el.scrollHeight;
  }, [open, actionLog?.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(
    () => () => {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    []
  );

  if (!open) return null;

  const list = Array.isArray(actionLog) ? actionLog : [];

  return createPortal(
    <>
      <div className="battle-action-log-drawer-root" role="presentation">
        <button
          type="button"
          className="battle-action-log-drawer-backdrop"
          aria-label={t('battleUI.actionHistory.drawerClose')}
          onClick={onClose}
        />
        <div
          className="battle-action-log-drawer-panel"
          role="dialog"
          aria-modal="true"
          aria-label={t('battleUI.actionHistory.drawerTitle')}
        >
          <div className="battle-action-log-drawer-header">
            <h2 className="battle-action-log-drawer-title">{t('battleUI.actionHistory.drawerTitle')}</h2>
            <button type="button" className="battle-action-log-drawer-close" onClick={onClose}>
              ×
            </button>
          </div>
          <div className="battle-action-log-drawer-scroll" ref={scrollRef} role="list">
            {list.length === 0 ? (
              <div className="battle-action-log-drawer-empty">{t('battleUI.actionHistory.noEvents')}</div>
            ) : (
              list.map((entry, idx) => (
                <BattleActionLogRow
                  key={entry.sequence != null ? `seq-${entry.sequence}` : `i-${idx}`}
                  entry={entry}
                  gameState={gameState}
                  userId={userId}
                  opponentId={opponentId}
                  currentUsername={currentUsername}
                  opponentUsername={opponentUsername}
                  isMobile={isMobile}
                  onExpandOrder={isMobile ? null : onExpandOrder}
                  onExpandOrderClear={isMobile ? null : onExpandOrderClear}
                />
              ))
            )}
          </div>
        </div>
      </div>
      {expandedOrder && !isMobile && fixedDesktopExpandedPosition ? (
        <div className="battle-action-log-drawer-expand-anchor">
          <OrderDesign
            order={expandedOrder}
            count={0}
            minimal={false}
            gameState={gameState}
            userId={userId}
            zoneName="tooltip"
            fixedDesktopExpandedPosition={fixedDesktopExpandedPosition}
            scale={orderDesignScale}
            hideExpandedPortalSourceSlot
          />
        </div>
      ) : null}
    </>,
    document.body
  );
}
